import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { ApplicationConfig, LOCALE_ID, provideAppInitializer } from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { PreloadAllModules, provideRouter, withEnabledBlockingInitialNavigation, withInMemoryScrolling, withPreloading } from '@angular/router';
import Aura from '@primeuix/themes/aura';
import { providePrimeNG } from 'primeng/config';
import { appRoutes } from './app.routes';
import { AuthService } from '@/core/auth/auth.service';
import { initializeAuth } from '@/core/auth/auth.initializer';
import { initializeLayout } from '@/layout/service/layout.initializer';
import { MessageService } from 'primeng/api';
import { appErrorMessageInterceptor } from './interceptors/app-error-message-interceptor';
import { dedupRequisicoesInterceptor } from './interceptors/dedup-requisicoes-interceptor';
import { idempotenciaInterceptor } from './interceptors/idempotencia-interceptor';
import { UI_ERROR_PARSERS, UiErrorMessageBuilder } from './handlers/error/ui-error-message-builder';
import { ArrecadacaoErrorParser } from './handlers/error/parsers/arrecadacao';
import { IGNORE_ERRORS, IgnoreErrorParser, IgnoreMeta, SilenciadorDeErrosEmLote, ignorarErrosDeOperacaoEmLote } from './handlers/error/parsers/ignore-message';
import { SICOM_PATHS } from './sicom-paths';
import { httpAuthInterceptor } from './interceptors/auth-interceptor';
import { ReceitaErrorParser } from './handlers/error/parsers/receita';
import { RelatorioErrorParser } from './handlers/error/parsers/relatorio';
import { PrestadorErrorParser } from './handlers/error/parsers/prestador';
import { ComissoesErrorParser } from './handlers/error/parsers/comissoes';
import { ConfiguracoesErrorParser } from './handlers/error/parsers/configuracoes';
import { initializeReportJobToasts, provideReportJobToastSources } from '@/core/services/report-job-toast.service';
import { provideAnimations } from '@angular/platform-browser/animations';
import { initializeProcessarRetornoJobToasts } from '@/core/services/processar-retorno-job-toast.service';
import { initializeGerarRemessaJobToasts } from '@/core/services/gerar-remessa-job-toast.service';

const errorResponsesIgnore = [
    (im: IgnoreMeta) => {
        return im.path.includes(SICOM_PATHS.portal.layout.base);
    },
    // Roles por usuário (carregar/atribuir/remover): o AtribuirRoles trata o erro
    // (mensagem inline no load; toast próprio no salvar) — evita toast global duplicado.
    (im: IgnoreMeta) => /\/usuarios\/[^/]+\/roles(\/|$)/.test(im.path),
    // Alteração de situação do prestador: o 409 (title INADIMPLENCIA_CONFIRMAR) é tratado
    // localmente pelo diálogo "Alterar situação" (abre confirmação e reenvia com
    // confirmaInadimplencia=true) — não deve gerar toast de erro genérico.
    (im: IgnoreMeta) => im.status === 409 && im.path === SICOM_PATHS.registro.prestador.situacao
].map((ignoreCallback) => ({ provide: IGNORE_ERRORS, multi: true, useValue: ignoreCallback }));

/**
 * Onde moram os endpoints que o encerramento em LOTE percorre por item: a consulta e a finalização da
 * solicitação (todo serviço de PF/PJ está sob uma destas duas bases) e o `mover` do quadro.
 *
 * São BASES, não os três endereços exatos — cada uma cobre dezenas de endpoints de pessoa física, de
 * pessoa jurídica e do kanban. Cobrir a mais é deliberado: o caminho por item passa por um serviço
 * DIFERENTE a cada `codigoServico`, e uma lista de paths exatos precisaria ser reeditada a cada
 * serviço novo — esquecê-la devolveria os toasts empilhados que a janela existe para calar. O que
 * limita o alcance não é a precisão do prefixo, é a JANELA: fora dela (`SilenciadorDeErrosEmLote`
 * fechado) nada aqui muda, e o fluxo unitário dos mesmos endpoints segue com toast.
 */
const PREFIXOS_DO_ENCERRAMENTO_EM_LOTE: readonly string[] = [SICOM_PATHS.servicos.pessoaFisica.base, SICOM_PATHS.servicos.pessoaJuridica.base, SICOM_PATHS.servicos.kanban];

/**
 * Enquanto o lote roda, o diálogo dele é quem relata os erros — um por item, com a mensagem real do
 * backend, numa lista que fica na tela. O toast global empilharia um por falha POR CIMA dessa lista.
 * Fora da janela (o fluxo unitário dos MESMOS endpoints) nada muda: lá o toast é a única notícia.
 */
const ignorarErrosDoLote = {
    provide: IGNORE_ERRORS,
    multi: true,
    useFactory: (silenciador: SilenciadorDeErrosEmLote) => ignorarErrosDeOperacaoEmLote(silenciador, PREFIXOS_DO_ENCERRAMENTO_EM_LOTE),
    deps: [SilenciadorDeErrosEmLote]
};

export const appConfig: ApplicationConfig = {
    providers: [
        MessageService,
        provideAppInitializer(() => initializeAuth()()),
        AuthService,
        UiErrorMessageBuilder,
        ...errorResponsesIgnore,
        ignorarErrosDoLote,
        {
            provide: UI_ERROR_PARSERS,
            multi: true,
            useClass: IgnoreErrorParser
        },
        { provide: UI_ERROR_PARSERS, useClass: ArrecadacaoErrorParser, multi: true },
        { provide: UI_ERROR_PARSERS, useClass: PrestadorErrorParser, multi: true },
        { provide: UI_ERROR_PARSERS, useClass: ReceitaErrorParser, multi: true },
        { provide: UI_ERROR_PARSERS, useClass: RelatorioErrorParser, multi: true },
        { provide: UI_ERROR_PARSERS, useClass: ComissoesErrorParser, multi: true },
        // Por último: só assume a leitura das telas de configuração que nenhum parser de módulo
        // reivindicou, e ainda assim cede a vez quando o backend manda mensagem própria.
        { provide: UI_ERROR_PARSERS, useClass: ConfiguracoesErrorParser, multi: true },
        provideRouter(
            appRoutes,
            withInMemoryScrolling({
                anchorScrolling: 'enabled',
                scrollPositionRestoration: 'enabled'
            }),
            withEnabledBlockingInitialNavigation(),
            withPreloading(PreloadAllModules)
        ),
        // A ordem importa. `dedupRequisicoesInterceptor` vem primeiro para registrar a requisição de
        // forma síncrona, antes de qualquer trabalho assíncrono: o de autenticação resolve token com
        // `from(...)`, e o duplo clique escaparia se a primeira chamada ainda estivesse presa lá sem
        // ter se registrado. Ficando na frente, tudo que vem depois — inclusive o toast de erro —
        // roda uma vez só para as chamadas coalescidas, em vez de uma vez por clique.
        provideHttpClient(withInterceptors([dedupRequisicoesInterceptor, idempotenciaInterceptor, httpAuthInterceptor, appErrorMessageInterceptor])),
        provideAnimations(),
        { provide: LOCALE_ID, useValue: 'pt-BR' },
        providePrimeNG({
            theme: { preset: Aura, options: { darkModeSelector: '.app-dark' } },
            translation: {
                monthNames: ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'],
                monthNamesShort: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'],
                dayNames: ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'],
                dayNamesShort: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'],
                dayNamesMin: ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'],
                /*
                 * Sem isto o PrimeNG cai nos textos em inglês: o botão de fechar dos diálogos
                 * ficava sem nome acessível em português e o paginador anunciava
                 * "First Page"/"Rows per page" a quem usa leitor de tela.
                 */
                aria: {
                    trueLabel: 'Sim',
                    falseLabel: 'Não',
                    nullLabel: 'Não selecionado',
                    selectAll: 'Selecionar todos os itens',
                    unselectAll: 'Desmarcar todos os itens',
                    close: 'Fechar',
                    previous: 'Anterior',
                    next: 'Próximo',
                    navigation: 'Navegação',
                    scrollTop: 'Ir para o topo',
                    firstPageLabel: 'Primeira página',
                    lastPageLabel: 'Última página',
                    nextPageLabel: 'Próxima página',
                    previousPageLabel: 'Página anterior',
                    prevPageLabel: 'Página anterior',
                    pageLabel: 'Página {page}',
                    rowsPerPageLabel: 'Registros por página',
                    jumpToPageDropdownLabel: 'Ir para a página',
                    jumpToPageInputLabel: 'Ir para a página'
                }
            }
        }),
        provideAppInitializer(initializeLayout),
        provideAppInitializer(initializeReportJobToasts),
        provideAppInitializer(initializeProcessarRetornoJobToasts),
        provideAppInitializer(initializeGerarRemessaJobToasts),
        provideReportJobToastSources()
    ]
};
